import { useRef, useCallback, useState, useEffect } from 'react';

import { motion, useInView } from 'motion/react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useScrollToSection } from '../hooks/useScrollToSection';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from './ui/carousel';

export function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const { scrollToSection } = useScrollToSection();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  // Autoplay
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 4000);
    return () => clearInterval(interval);
  }, [api]);

  // Track current slide for dots
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on('select', () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  const [services, setServices] = useState([
    {
      id: '1',
      title: 'Alquiler y venta de Ofi-Bodegas Clase A',
      description: 'Espacios diseñados para integrar almacenamiento, oficinas y operación en un solo lugar. Ideales para empresas logísticas, distribución y comercio.',
      image: '/images/znl-1.webp',
      meta: 'Ofi-Bodegas',
    },
    {
      id: '2',
      title: 'Infraestructura Moderna',
      description: 'Diseño optimizado para flujo de carga · Alturas adecuadas para almacenamiento eficiente · Áreas de maniobra para camiones.',
      image: '/images/znl2.webp',
      meta: 'Moderna',
    },
    {
      id: '3',
      title: 'Ubicación Estratégica',
      description: 'Dentro de la Zona Libre de Colón, con acceso directo a puertos, transporte terrestre y conexiones internacionales.',
      image: '/images/znl3.webp',
      meta: 'Estratégica',
    },
    {
      id: '4',
      title: 'Flexibilidad Operativa',
      description: 'Espacios adaptables según necesidades del cliente: almacenamiento, oficinas administrativas o uso mixto.',
      image: '/images/znl4.webp',
      meta: 'Operativa',
    },
    {
      id: '5',
      title: 'Potencial de Crecimiento',
      description: 'Proyecto pensado para escalabilidad y expansión futura dentro del mismo parque.',
      image: '/images/master-corregido.webp',
      meta: 'Potencial',
    },
  ]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/services.php')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.services?.length > 0) {
          setServices(data.services);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="services" className="py-20 md:py-32 bg-white border-t border-slate-200" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-flex items-center px-4 py-2 rounded-full border border-primary/10 bg-gradient-to-br from-white to-slate-50 text-slate-700 mb-6 shadow-sm font-medium text-sm"
          >
            Servicios
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl text-slate-950 mb-5 tracking-tight font-bold"
          >
            Soluciones Logísticas
          </motion.h2>

        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
        >
          <Carousel setApi={setApi} opts={{ align: 'start', loop: true }} className="w-full">
            <CarouselContent className="-ml-4">
              {services.map((service, index) => (
                <CarouselItem key={service.id || index} className="pl-4 md:basis-1/2 lg:basis-1/3" role="group" aria-label={service.title}>
                  <motion.article
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * index }}
                    whileHover={{ y: -6 }}
                    className="h-full"
                  >
                    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow h-full">
                      <div className="relative h-52 overflow-hidden">
                        <ImageWithFallback src={service.image} alt={service.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-slate-950/10 to-transparent" />
                        <div className="absolute left-4 top-4 rounded-full border border-white/40 bg-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white backdrop-blur-sm">
                          {service.meta}
                        </div>
                      </div>

                      <div className="p-6">
                        <h3 className="text-3xl text-slate-950 mb-3 font-semibold tracking-tight leading-tight">{service.title}</h3>
                        <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>


                      </div>
                    </div>
                  </motion.article>
                </CarouselItem>
              ))}
            </CarouselContent>

            <div className="flex flex-col items-center gap-4 mt-8">
              <div className="flex justify-center gap-4">
                <CarouselPrevious className="relative left-0 translate-x-0 bg-white border-slate-200 text-slate-900 hover:bg-slate-50" />
                <CarouselNext className="relative right-0 translate-x-0 bg-white border-slate-200 text-slate-900 hover:bg-slate-50" />
              </div>
              {/* Pagination dots */}
              <div className="flex gap-2" role="tablist" aria-label="Servicios">
                {services.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    aria-selected={current === index}
                    aria-label={`Servicio ${index + 1}`}
                    onClick={() => api?.scrollTo(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      current === index ? 'w-8 bg-primary' : 'w-2 bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </Carousel>
        </motion.div>

      </div>
    </section>
  );
}
